import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description }) => (
  <div className="relative border-2 border-[#b5badd] rounded-lg p-6 lg:p-8 bg-white hover:shadow-lg transition-shadow overflow-hidden">
    {/* Circle decoration - bottom left corner */}
    <div className="absolute -left-16 -bottom-16 w-[200px] h-[200px] pointer-events-none opacity-30">
      <img
        src={require('../assets/img/services/circle.svg').default}
        alt=""
        className="w-full h-full object-contain"
      />
    </div>

    <div className="flex items-start gap-6 relative z-10">
      {/* Icon */}
      <div className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 flex items-center justify-center">
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1">
        <h3 className="font-exo font-bold text-ebony-clay text-xl lg:text-2xl tracking-[1.2px] leading-normal mb-3">
          {title}
        </h3>
        <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal">
          {description}
        </p>
      </div>
    </div>
  </div>
);

const ServicesPage: React.FC = () => {
  const services = [
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="32" fill="#4452e3" opacity="0.1" />
          <path d="M24 22L28 22L28 24L24 24L24 22ZM30 22L34 22L34 24L30 24L30 22ZM36 22L40 22L40 24L36 24L36 22Z" fill="#4452e3" />
          <path d="M20 26L44 26L44 42L20 42L20 26Z" stroke="#4452e3" strokeWidth="2" fill="none" />
          <path d="M22 20L42 20L42 26L22 26L22 20Z" fill="#4452e3" opacity="0.3" />
          <text x="32" y="36" fontSize="16" fontWeight="bold" fill="#4452e3" textAnchor="middle">&lt;/&gt;</text>
        </svg>
      ),
      title: 'Web Development',
      description: 'Custom web applications built with modern frameworks and best practices.',
    },
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="32" fill="#4452e3" opacity="0.1" />
          <circle cx="32" cy="32" r="16" stroke="#4452e3" strokeWidth="2" fill="none" />
          <circle cx="32" cy="20" r="2" fill="#4452e3" />
          <circle cx="32" cy="44" r="2" fill="#4452e3" />
          <circle cx="20" cy="32" r="2" fill="#4452e3" />
          <circle cx="44" cy="32" r="2" fill="#4452e3" />
          <circle cx="25" cy="25" r="2" fill="#4452e3" />
          <circle cx="39" cy="39" r="2" fill="#4452e3" />
          <circle cx="39" cy="25" r="2" fill="#4452e3" />
          <circle cx="25" cy="39" r="2" fill="#4452e3" />
          <circle cx="32" cy="32" r="4" fill="#4452e3" />
        </svg>
      ),
      title: 'AI/ML Solutions',
      description: 'Leverage artificial intelligence to automate and optimize your business processes.',
    },
    {
      icon: (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="32" fill="#4452e3" opacity="0.1" />
          <path d="M28 22C28 20.8954 28.8954 20 30 20H34C35.1046 20 36 20.8954 36 22V24H28V22Z" fill="#4452e3" opacity="0.3" />
          <rect x="20" y="24" width="24" height="16" rx="2" stroke="#4452e3" strokeWidth="2" fill="none" />
          <path d="M22 28H42V36H22V28Z" fill="#4452e3" opacity="0.2" />
          <path d="M24 30H28V34H24V30Z" fill="#4452e3" />
          <path d="M30 30H34V34H30V30Z" fill="#4452e3" />
          <path d="M36 30H40V34H36V30Z" fill="#4452e3" />
        </svg>
      ),
      title: 'Cloud Services',
      description: 'Scalable cloud infrastructure and migration services for your enterprise.',
    },
  ];

  return (
    <div className="w-full bg-white">
      <Navbar />

      {/* Services Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-content mx-auto px-6">
          <h1 className="font-exo font-bold text-ebony-clay text-3xl lg:text-[48px] tracking-[2.4px] leading-tight mb-4">
            Services
          </h1>
          <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-normal mb-12 lg:mb-16">
            Transform your business with our comprehensive technology solutions
          </p>

          {/* Service Cards */}
          <div className="space-y-6 lg:space-y-8">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
